import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, InputSuelto, SelectSuelto } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { Tabs, TabPanel } from '@/ui/Tabs';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  CATALOGOS_TIPADOS, ENUMERACIONES,
  useBorrarElementoDeCatalogo, useBorrarEnumeracion, useCatalogo, useEnumeracion,
  useGuardarElementoDeCatalogo, useGuardarEnumeracion, useSembrarCatalogos,
  type ElementoDeCatalogo,
} from './api';

/**
 * Catálogos maestros — empresas, sedes, cargos y las enumeraciones sueltas.
 *
 * ## Lo que cambia
 *
 * - **La pestaña activa ya no se marca comparando textos del DOM.** El vanilla
 *   lo hacía así: `b.textContent.trim() === _CAT_LABELS[t]`. Renombrar una
 *   pestaña rompía el subrayado, y las dos pestañas con un nombre parecido se
 *   marcaban juntas.
 * - **Cada lista dice si falló.** `_renderEnumList` tenía un `catch{}` vacío:
 *   un error de red dejaba «Sin valores aún», que es lo mismo que enseña un
 *   catálogo legítimamente vacío.
 * - **Desactivar pregunta con el nombre del valor**, no con «¿Desactivar este
 *   valor?» — ya se ha desactivado el equivocado por eso.
 */
export function Catalogos() {
  const [pestana, setPestana] = useState<string>(CATALOGOS_TIPADOS[0].clave);
  const sembrar = useSembrarCatalogos();

  const pestanas = [
    ...CATALOGOS_TIPADOS.map((c) => ({ clave: c.clave, etiqueta: c.etiqueta })),
    { clave: 'enums', etiqueta: 'Enumeraciones' },
  ];

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-4 font-bold text-brand-ink">Catálogos maestros</h2>
          <p className="mt-1 text-3 text-muted">
            Gobierno del dato: empresas, sedes, cargos y enumeraciones. Sustituyen al texto
            libre, así que dos formas de escribir lo mismo dejan de ser dos valores.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          loading={sembrar.isPending}
          onClick={() => sembrar.mutate(undefined, {
            onSuccess: (r) => {
              const n = r.created?.positions ?? 0;
              Utils.toast(n === 1
                ? 'Sembrado 1 cargo desde los usuarios'
                : `Sembrados ${n} cargos desde los usuarios`, 'success');
            },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          })}
        >
          <i className="bi bi-magic" aria-hidden="true" /> Sembrar desde datos
        </Button>
      </header>

      <Tabs pestanas={pestanas} valor={pestana} onCambio={setPestana}>
        {CATALOGOS_TIPADOS.map((c) => (
          <TabPanel key={c.clave} clave={c.clave}>
            <CatalogoTipado config={c} />
          </TabPanel>
        ))}
        <TabPanel clave="enums">
          <Enumeraciones />
        </TabPanel>
      </Tabs>
    </>
  );
}

type Config = (typeof CATALOGOS_TIPADOS)[number];

function CatalogoTipado({ config }: { config: Config }) {
  const consulta = useCatalogo(config.clave, true);
  const guardar = useGuardarElementoDeCatalogo();
  const borrar = useBorrarElementoDeCatalogo();

  const [nombre, setNombre] = useState('');
  const [extra, setExtra] = useState('');
  const [editando, setEditando] = useState<ElementoDeCatalogo | null>(null);
  const [desactivando, setDesactivando] = useState<ElementoDeCatalogo | null>(null);

  const items = consulta.data ?? [];
  const singular = config.etiqueta.toLowerCase().replace(/s$/, '');

  function agregar() {
    const limpio = nombre.trim();
    if (!limpio) { Utils.toast('Escribe un nombre', 'warning'); return; }
    guardar.mutate({
      tipo: config.clave,
      id: null,
      cuerpo: { name: limpio, [config.extra]: extra.trim() || null },
    }, {
      onSuccess: () => { setNombre(''); setExtra(''); Utils.toast('Agregado', 'success'); },
      onError: (e) => Utils.toast((e as Error).message, 'error'),
    });
  }

  return (
    <>
      <form
        className="mb-3 flex flex-wrap gap-2"
        onSubmit={(e) => { e.preventDefault(); agregar(); }}
      >
        <InputSuelto
          className="min-w-40 flex-1"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={`Nombre de ${singular}`}
          aria-label={`Nombre de ${singular}`}
        />
        <InputSuelto
          className="w-36"
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          placeholder={config.extraEtiqueta}
          aria-label={`${config.extraEtiqueta} de ${singular}`}
        />
        <Button type="submit" size="sm" loading={guardar.isPending}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar
        </Button>
      </form>

      {consulta.isPending ? (
        <SkeletonFilas filas={5} columnas={3} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que={`el catálogo de ${config.etiqueta.toLowerCase()}`}
          onReintentar={() => void consulta.refetch()}
        />
      ) : !items.length ? (
        <EstadoVacio
          icono="bi-tags"
          titulo="Sin valores"
          descripcion='Agrega el primero, o usa «Sembrar desde datos».'
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-3">
            <caption className="sr-only">{config.etiqueta} del catálogo</caption>
            <thead className="bg-sunken text-2">
              <tr>
                <th scope="col" className="p-2 text-left font-semibold">Nombre</th>
                <th scope="col" className="p-2 text-left font-semibold">
                  {config.extraEtiqueta}
                </th>
                <th scope="col" className="p-2 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-line">
                  <td className="p-2">{i.name}</td>
                  <td className="p-2 text-muted">{String(i[config.extra] ?? '') || '—'}</td>
                  <td className="whitespace-nowrap p-1 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditando(i)}>
                      <i className="bi bi-pencil" aria-hidden="true" />
                      <span className="sr-only">Editar {i.name}</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDesactivando(i)}>
                      <i className="bi bi-slash-circle text-danger-ink" aria-hidden="true" />
                      <span className="sr-only">Desactivar {i.name}</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editando && (
        <EditorDeValor
          config={config}
          item={editando}
          onCerrar={() => setEditando(null)}
        />
      )}

      <ConfirmDialog
        abierto={desactivando != null}
        onCambio={(v) => { if (!v) setDesactivando(null); }}
        titulo={`Desactivar ${singular}`}
        // Con el nombre dentro. «¿Desactivar este valor?» no permite comprobar
        // que se pulsó la fila que se creía.
        descripcion={`«${desactivando?.name ?? ''}» deja de ofrecerse en los formularios. Los registros que ya lo usan no cambian.`}
        textoConfirmar="Desactivar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!desactivando) return;
          borrar.mutate({ tipo: config.clave, id: desactivando.id }, {
            onSuccess: () => { Utils.toast('Desactivado', 'success'); setDesactivando(null); },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          });
        }}
      />
    </>
  );
}

function EditorDeValor({ config, item, onCerrar }: {
  config: Config;
  item: ElementoDeCatalogo;
  onCerrar: () => void;
}) {
  const guardar = useGuardarElementoDeCatalogo();
  const [nombre, setNombre] = useState(item.name);
  const [extra, setExtra] = useState(String(item[config.extra] ?? ''));

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={`Editar ${config.etiqueta.toLowerCase().replace(/s$/, '')}`}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={guardar.isPending}
            onClick={() => {
              const limpio = nombre.trim();
              if (!limpio) { Utils.toast('Escribe un nombre', 'warning'); return; }
              guardar.mutate({
                tipo: config.clave,
                id: item.id,
                cuerpo: { name: limpio, [config.extra]: extra.trim() || null },
              }, {
                onSuccess: () => { Utils.toast('Actualizado', 'success'); onCerrar(); },
                onError: (e) => Utils.toast((e as Error).message, 'error'),
              });
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Nombre">
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </Field>
        <Field label={config.extraEtiqueta} opcional>
          <Input value={extra} onChange={(e) => setExtra(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** Las enumeraciones sueltas: sexo, estado civil, parentesco… Una tabla sin
 *  columnas propias, elegida con un desplegable. */
function Enumeraciones() {
  const [tipo, setTipo] = useState<string>(ENUMERACIONES[0][0]);
  const consulta = useEnumeracion(tipo, true);
  const guardar = useGuardarEnumeracion();
  const borrar = useBorrarEnumeracion();

  const [nombre, setNombre] = useState('');
  const [editando, setEditando] = useState<ElementoDeCatalogo | null>(null);
  const [desactivando, setDesactivando] = useState<ElementoDeCatalogo | null>(null);
  const [nuevoNombre, setNuevoNombre] = useState('');

  const items = consulta.data ?? [];
  const etiqueta = ENUMERACIONES.find(([k]) => k === tipo)?.[1] ?? tipo;

  return (
    <>
      <form
        className="mb-3 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const limpio = nombre.trim();
          if (!limpio) { Utils.toast('Escribe un valor', 'warning'); return; }
          guardar.mutate({ id: null, tipo, name: limpio }, {
            onSuccess: () => { setNombre(''); Utils.toast('Agregado', 'success'); },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          });
        }}
      >
        <SelectSuelto
          className="w-48"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          aria-label="Enumeración a editar"
        >
          {ENUMERACIONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </SelectSuelto>
        <InputSuelto
          className="min-w-40 flex-1"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Nuevo valor"
          aria-label={`Nuevo valor de ${etiqueta}`}
        />
        <Button type="submit" size="sm" loading={guardar.isPending}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar
        </Button>
      </form>

      {consulta.isPending ? (
        <SkeletonFilas filas={4} columnas={2} />
      ) : consulta.isError ? (
        /* El vanilla se lo comía con un `catch{}` vacío y enseñaba «Sin valores
           aún»: el mismo mensaje que un catálogo legítimamente vacío. */
        <EstadoError
          err={consulta.error}
          que={`los valores de ${etiqueta.toLowerCase()}`}
          onReintentar={() => void consulta.refetch()}
        />
      ) : !items.length ? (
        <p className="text-3 text-muted">Sin valores aún.</p>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-3">
            <caption className="sr-only">Valores de {etiqueta}</caption>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-line first:border-t-0">
                  <td className="p-2">{i.name}</td>
                  <td className="whitespace-nowrap p-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditando(i); setNuevoNombre(i.name); }}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                      <span className="sr-only">Editar {i.name}</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDesactivando(i)}>
                      <i className="bi bi-slash-circle text-danger-ink" aria-hidden="true" />
                      <span className="sr-only">Desactivar {i.name}</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editando && (
        <Dialog
          abierto
          onCambio={(v) => { if (!v) setEditando(null); }}
          titulo="Editar valor"
          pie={
            <>
              <Button variant="ghost" onClick={() => setEditando(null)}>Cancelar</Button>
              <Button
                loading={guardar.isPending}
                onClick={() => {
                  const limpio = nuevoNombre.trim();
                  if (!limpio) { Utils.toast('Escribe un valor', 'warning'); return; }
                  guardar.mutate({ id: editando.id, tipo, name: limpio }, {
                    onSuccess: () => { Utils.toast('Actualizado', 'success'); setEditando(null); },
                    onError: (e) => Utils.toast((e as Error).message, 'error'),
                  });
                }}
              >
                Guardar
              </Button>
            </>
          }
        >
          <Field label="Nombre">
            <Input value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)} />
          </Field>
        </Dialog>
      )}

      <ConfirmDialog
        abierto={desactivando != null}
        onCambio={(v) => { if (!v) setDesactivando(null); }}
        titulo="Desactivar valor"
        descripcion={`«${desactivando?.name ?? ''}» deja de ofrecerse en los formularios. Los registros que ya lo usan no cambian.`}
        textoConfirmar="Desactivar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!desactivando) return;
          borrar.mutate(desactivando.id, {
            onSuccess: () => { Utils.toast('Desactivado', 'success'); setDesactivando(null); },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          });
        }}
      />
    </>
  );
}
